/**
 * AdminAuth Component
 *
 * Simple admin authentication form with basic password protection.
 * Designed for immediate security while preparing for contractor enhancement.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Input, Form, Alert, Card, CardBody, CardFooter } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';
import { useAuth } from '@/hooks/useAuth';

/**
 * Admin Authentication Component
 *
 * Features:
 * - Email-based authentication with Supabase Auth
 * - Role-based access control (app_metadata.role = 'admin')
 * - Secure password hashing handled by Supabase
 * - HeroUI Form and Input components with validation
 * - Real-time email/password validation with clear error messaging
 * - Return URL preservation after authentication
 * - Mobile responsive and accessible design
 *
 * Authentication: Uses Supabase Auth with admin role check
 * Admin users must have app_metadata.role = 'admin' in auth.users
 */
export function AdminAuth(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, isAuthenticated, user } = useAuth();

  // All hooks must be called before any early returns
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  // Get intended admin destination from location state
  const from = (location.state as { from?: string })?.from || '/admin';

  // Watch for successful authentication (including after MFA completion)
  React.useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user is admin
      if (user.role === 'admin') {
        console.log('✅ AdminAuth: User authenticated as admin, navigating to', from);
        navigate(from, { replace: true });
      } else {
        console.error('❌ AdminAuth: Authenticated user is not an admin, signing out');
        logout();
        setErrorMessage('Access denied. Admin credentials required.');
      }
    }
  }, [isAuthenticated, user, from, navigate, logout]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(prev => !prev);
  }, []);

  // Enforce admin subdomain access only (allow dev for testing)
  const hostname = window.location.hostname;
  // TODO: Re-enable subdomain check for production
  // Temporarily disabled for local testing on localhost
  if (!hostname.startsWith('admin.') && !hostname.startsWith('dev.') && hostname !== 'localhost' && hostname !== '127.0.0.1') {
    console.log('🚫 AdminAuth: Admin access only allowed on admin.* or dev.* subdomains');
    navigate('/', { replace: true });
    return <div>Redirecting...</div>;
  }

  // Simple field validation
  const validateEmail = (value: string): string | null => {
    if (!value) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
    return null;
  };

  const validatePassword = (value: string): string | null => {
    if (!value) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters";
    return null;
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Validate field formats
      const emailError = validateEmail(email);
      const passwordError = validatePassword(password);

      if (emailError) {
        setErrorMessage(emailError);
        return;
      }

      if (passwordError) {
        setErrorMessage(passwordError);
        return;
      }

      // Use AuthProvider's login method (handles MFA automatically)
      console.log('🔐 AdminAuth: Attempting login for', email);
      const result = await login({
        email,
        password,
        rememberMe: false
      });

      // Handle MFA required - modal will show automatically via AuthProvider
      if (!result.success && result.error === 'MFA_REQUIRED') {
        console.log('🔐 AdminAuth: MFA required, waiting for verification...');
        // Don't show error - MFALoginChallengeModal will handle this
        // useEffect will handle navigation after MFA completion
        setIsSubmitting(false);
        return;
      }

      // Handle other auth errors
      if (!result.success) {
        setErrorMessage(result.message || 'Invalid email or password. Please try again.');
        console.log('❌ AdminAuth: Login failed:', result.error);
        return;
      }

      // Login successful - useEffect will handle admin check and navigation
      console.log('✅ AdminAuth: Login successful, useEffect will handle navigation');

    } catch (error) {
      console.error('❌ AdminAuth: Authentication error:', error);
      setErrorMessage('Authentication error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <Form
            validationBehavior="aria"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
          {/* Admin Email Input */}
          <Input
            type="email"
            label="Admin Email"
            placeholder="Enter admin email"
            value={email}
            onValueChange={setEmail}
            validate={validateEmail}
            isRequired
            isClearable
            autoComplete="email"
            errorMessage={hasSubmitted ? validateEmail(email) : undefined}
            isInvalid={hasSubmitted && !!validateEmail(email)}
            classNames={{
              input: "text-base", // 16px font size to prevent mobile zoom
            }}
          />

          {/* Admin Password Input */}
          <Input
            type={isPasswordVisible ? "text" : "password"}
            label="Password"
            placeholder="Enter admin password"
            value={password}
            onValueChange={setPassword}
            validate={validatePassword}
            isRequired
            autoComplete="current-password"
            errorMessage={hasSubmitted ? validatePassword(password) : undefined}
            isInvalid={hasSubmitted && !!validatePassword(password)}
            endContent={
              <button
                className="focus:outline-none"
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
              >
                {isPasswordVisible ? (
                  <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                ) : (
                  <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                )}
              </button>
            }
            classNames={{
              input: "text-base", // 16px font size to prevent mobile zoom
            }}
          />

          {/* Error Display */}
          {errorMessage && (
            <Alert
              color="warning"
              variant="flat"
              description={errorMessage}
            />
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            color="primary"
            size="lg"
            isLoading={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Authenticating...' : 'Access Admin Console'}
          </Button>
          </Form>
        </CardBody>

        {/* Help Text */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center space-y-2">
            <p className="text-sm text-text-secondary">
              {`Admin access required for ${Brand.displayName} administration console`}
            </p>
            <p className="text-xs text-text-tertiary">
              Development Mode: Check environment configuration for credentials
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}